import React, { useState, useEffect } from "react";
import Cookies from 'js-cookie';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaStar } from "react-icons/fa";
import { Button, IconButton } from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { FormattedMessage, useIntl } from "react-intl";
import "./Reviews.scss";

const colors = {
    orange: "#FFBA5A",
    grey: "#a9a9a9",
};

const API_URL = process.env.REACT_APP_API_URL;

const ReviewCard = ({ review }) => (
    <div className="review-card">
        <div className="card-header">
            <div className="review-title">{review.title}</div>
            <div className="review-stars">
                {Array.from({ length: review.stars }).map((_, si) => (
                    <FaStar key={si} size={18} color={colors.orange} />
                ))}
            </div>
        </div>
        <div className="review-author">{review.name}</div>
        <div className="review-text">{review.review}</div>
    </div>
);

const MemoizedReviewCard = React.memo(ReviewCard);

const ReviewsDrawer = ({ productId = null }) => {
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("read"); // 'read' or 'create'
    const type = productId ? "product" : "store";
    const [review, setReview] = useState({ title: "", name: "", review: "", stars: 1 });
    const [reviews, setReviews] = useState([]);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [currentValue, setCurrentValue] = useState(0);
    const [hoverValue, setHoverValue] = useState(undefined);

    const fetchReviews = async () => {
        try {
            let url;
            if (productId) {
                url = `${API_URL}/reviews?filters[type][$eq]=product&filters[product][id][$eq]=${productId}&filters[approval][$eq]=true&populate=product`;
            } else {
                url = `${API_URL}/reviews?filters[type][$eq]=store&filters[approval][$eq]=true`;
            }

            const response = await fetch(url);
            const json = await response.json();
            return json.data || [];
        } catch (err) {
            console.error("Error fetching reviews:", err);
            return [];
        }
    };

    useEffect(() => {
        const getReviews = async () => {
            const fetchedReviews = await fetchReviews();
            setReviews(fetchedReviews);
        };
        getReviews();
    }, [productId]);

    const stars = Array(5).fill(0);

    const handleClick = (value) => {
        setCurrentValue(value);
    };

    const handleMouseOver = (value) => {
        setHoverValue(value);
    };

    const handleMouseLeave = () => {
        setHoverValue(undefined);
    };

    const createReview = async (event) => {
        event.preventDefault();
        const token = Cookies.get('token');

        if (!token) {
            setError(intl.formatMessage({ id: "review_login_required", defaultMessage: "Musisz być zalogowany, aby dodać recenzję." }));
            return;
        }

        try {
            const reviewData = {
                title: review.title,
                name: review.name,
                review: review.review,
                stars: review.stars,
                type: productId ? "product" : "store",
                approval: false,
            };

            if (productId) {
                reviewData.product = {
                    connect: [{ id: parseInt(productId) }]
                };
            }

            const response = await fetch(`${API_URL}/reviews`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    data: reviewData,
                }),
            });

            const result = await response.json();

            if (response.ok) {
                setMessage(intl.formatMessage({ id: "review_success_message", defaultMessage: "Dziękujemy! Twoja recenzja oczekuje na zatwierdzenie." }));
                setError("");
                setReview({ title: "", name: "", review: "", stars: 1 });
                setCurrentValue(0);
                setTimeout(() => {
                    setActiveTab("read");
                    setMessage("");
                }, 3000);
            } else {
                setError(result.error?.message || intl.formatMessage({ id: "review_submit_error", defaultMessage: "Nie udało się dodać recenzji." }));
                setMessage("");
            }
        } catch (error) {
            console.error("Error submitting review:", error);
            setError(intl.formatMessage({ id: "review_error_prefix", defaultMessage: "Wystąpił błąd: " }) + error.message);
            setMessage("");
        }
    };

    // Lock page scroll behind the open drawer so mobile users can't drag the page sideways.
    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const handleClose = () => {
        setOpen(false);
        setActiveTab("read");
        setError("");
        setMessage("");
    };

    return (
        <>
            {/* Accordion Button */}
            <div className="reviews-accordion">
                <button
                    className="accordion-button"
                    onClick={() => setOpen(true)}
                >
                    <FormattedMessage id="reviews_cta" defaultMessage="Poznaj opinie klientów lub oceń nasz sklep" />
                </button>
            </div>

            {/* Sliding Drawer */}
            <div className={`reviews-drawer ${open ? "active" : ""}`}>
                <div className="overlay" onClick={handleClose} />

                <div className="drawer__inner">
                    <div className="reviews-panel">
                        <div className="panel-header">
                            <h1><FormattedMessage id="reviews_heading" defaultMessage="Opinie" /></h1>
                            <IconButton
                                className="close-icon"
                                onClick={handleClose}
                                aria-label={intl.formatMessage({ id: "close" })}
                                sx={{ width: 44, height: 44, color: "text.secondary" }}
                            >
                                <CloseOutlinedIcon />
                            </IconButton>
                        </div>

                        {/* Tab Navigation */}
                        <div className="tab-navigation">
                            <button
                                className={`tab-btn ${activeTab === "read" ? "active" : ""}`}
                                onClick={() => setActiveTab("read")}
                            >
                                <FormattedMessage id="read_reviews" defaultMessage="Czytaj opinie" />
                            </button>
                            <button
                                className={`tab-btn ${activeTab === "create" ? "active" : ""}`}
                                onClick={() => setActiveTab("create")}
                            >
                                <FormattedMessage id="add_review" defaultMessage="Dodaj opinię" />
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="tab-content">
                            {activeTab === "read" ? (
                                <div className="read-reviews">
                                    {reviews.length > 0 ? (
                                        <div className="reviews-column">
                                            {reviews.map((review, i) => (
                                                <MemoizedReviewCard key={review.id || i} review={review} />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="no-reviews">
                                            <p><FormattedMessage id="no_reviews" defaultMessage="Brak recenzji. Bądź pierwszy, który zostawi opinię!" /></p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="create-review">
                                    <form onSubmit={createReview}>
                                        <h2>{type === "product"
                                            ? <FormattedMessage id="rate_product" defaultMessage="Oceń nasz produkt!" />
                                            : <FormattedMessage id="rate_store" defaultMessage="Oceń nasz sklep!" />}</h2>

                                        <div className="star-rating">
                                            {stars.map((_, index) => (
                                                <FaStar
                                                    key={index}
                                                    size={28}
                                                    className="star-icon"
                                                    color={
                                                        (hoverValue || currentValue) > index
                                                            ? colors.orange
                                                            : colors.grey
                                                    }
                                                    onClick={() => {
                                                        handleClick(index + 1);
                                                        setReview({ ...review, stars: index + 1 });
                                                    }}
                                                    onMouseOver={() => handleMouseOver(index + 1)}
                                                    onMouseLeave={handleMouseLeave}
                                                />
                                            ))}
                                        </div>

                                        <input
                                            type="text"
                                            placeholder={intl.formatMessage({ id: "review_title_placeholder", defaultMessage: "Tytuł" })}
                                            required
                                            className="review-input"
                                            value={review.title}
                                            onChange={(e) => setReview({ ...review, title: e.target.value })}
                                        />

                                        <input
                                            type="text"
                                            placeholder={intl.formatMessage({ id: "review_name_placeholder", defaultMessage: "Twoje imię" })}
                                            required
                                            className="review-input"
                                            value={review.name}
                                            onChange={(e) => setReview({ ...review, name: e.target.value })}
                                        />

                                        <textarea
                                            placeholder={intl.formatMessage({ id: "review_comment_placeholder", defaultMessage: "Jakie są Twoje uwagi?" })}
                                            required
                                            className="review-textarea"
                                            value={review.review}
                                            onChange={(e) => setReview({ ...review, review: e.target.value })}
                                        />

                                        <Button type="submit" className="submit-btn" variant="contained">
                                            <FormattedMessage id="submit_review" defaultMessage="Wyślij opinię" />
                                        </Button>

                                        {message && <p className="success-message">{message}</p>}
                                        {error && <p className="error-message">{error}</p>}
                                    </form>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ReviewsDrawer;
